import { DateTime } from 'luxon';
import type { Financial, Financials } from '@benzinga/securities-manager';

export type PeerMetrics = {
  symbol: string;
  pe: number | null;
  pb: number | null;
  eps: number | null;
  roe: number | null;
  de: number | null;
  grossMargin: number | null;
  netMargin: number | null;
  revenueGrowth: number | null;
  marketCap: number | null;
};

export const safeDiv = (
  numerator: number | null | undefined,
  denominator: number | null | undefined,
): number | null => {
  if (numerator == null || denominator == null || !isFinite(numerator) || !isFinite(denominator) || denominator === 0) {
    return null;
  }
  return numerator / denominator;
};

/**
 * Year-over-year growth of TTM revenue, as a ratio (0.1 = 10%), from a history of 12M financials.
 * Compares the latest report with the one closest to 12 months earlier (within ±1.5 months).
 * Fallback for when operationRatios.revenueGrowth is missing upstream (#16322).
 */
export const deriveRevenueGrowth = (financials: Financial[] | null | undefined): number | null => {
  const reports = (financials ?? [])
    .map(report => ({ date: DateTime.fromISO(report.asOf ?? ''), revenue: report.incomeStatement?.totalRevenue }))
    .filter(
      (report): report is { date: DateTime; revenue: number } =>
        report.date.isValid && report.revenue != null && isFinite(report.revenue),
    )
    .sort((left, right) => right.date.toMillis() - left.date.toMillis());

  const latest = reports[0];
  if (!latest) return null;

  let prior: { date: DateTime; revenue: number } | undefined;
  let priorDistance = Infinity;
  for (const report of reports.slice(1)) {
    const months = latest.date.diff(report.date, 'months').months;
    const distance = Math.abs(months - 12);
    if (distance <= 1.5 && distance < priorDistance) {
      prior = report;
      priorDistance = distance;
    }
  }

  // Latest revenue of 0 is a real -100%; negative revenue (or a non-positive base) has no meaningful growth.
  if (!prior || prior.revenue <= 0 || latest.revenue < 0) return null;
  return latest.revenue / prior.revenue - 1;
};

export const deriveMetrics = (fundamentals: Financials, revenueHistory?: Financial[]): PeerMetrics => {
  const symbol = String(fundamentals.symbol ?? fundamentals.id ?? '');
  const op = fundamentals.operationRatios?.[0];
  const val = fundamentals.valuationRatios?.[0];
  const earn = fundamentals.earningReports?.[0];
  const fin = fundamentals.financials?.[0];
  const income = fin?.incomeStatement;
  const balance = fin?.balanceSheet;
  const cash = fin?.cashFlowStatement;
  const profile = fundamentals.shareClassProfile;
  const ownership = fundamentals.ownership?.[0];

  const sharesOut = ownership?.sharesOutstanding ?? earn?.dilutedAverageShares ?? null;

  const bookEquity =
    balance?.commonStockEquity ??
    balance?.stockholdersEquity ??
    balance?.totalEquity ??
    (val?.bookValuePerShare != null && sharesOut != null ? val.bookValuePerShare * sharesOut : null);

  const roe = op?.roe ?? safeDiv(income?.netIncome, bookEquity) ?? safeDiv(earn?.basicEps, val?.bookValuePerShare);

  const derivedDebt =
    profile?.enterpriseValue != null && profile?.marketCap != null
      ? profile.enterpriseValue - profile.marketCap + (cash?.endCashPosition ?? balance?.cashAndCashEquivalents ?? 0)
      : null;
  const de =
    op?.totalDebtEquityRatio ??
    safeDiv(balance?.totalDebt, balance?.totalEquity ?? balance?.commonStockEquity) ??
    safeDiv(derivedDebt, bookEquity);

  const grossMargin = op?.grossMargin ?? safeDiv(income?.grossProfit, income?.totalRevenue);
  const netMargin = op?.netMargin ?? safeDiv(income?.netIncome, income?.totalRevenue);
  const revenueGrowth = op?.revenueGrowth ?? deriveRevenueGrowth(revenueHistory);

  return {
    symbol,
    pe: val?.peRatio ?? null,
    pb: val?.pbRatio ?? null,
    eps: earn?.basicEps ?? null,
    roe,
    de,
    grossMargin,
    netMargin,
    revenueGrowth,
    marketCap: profile?.marketCap ?? null,
  };
};
